import { useEffect, useRef, useState } from 'react';

import { orders as ordersApi } from '../api/endpoints.ts';
import type { OrderListItem } from '../api/types.ts';
import { formatPara, para, percentOf, rsd } from '../core/money.ts';
import { useStore } from '../core/store.ts';
import { errorText, useStrings } from '../i18n/index.ts';
import { paymentConfigStore } from '../state/payment.ts';
import { Icon } from '../ui/icons.tsx';
import { Button, cx, ErrorBanner, GlassButton } from '../ui/kit.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { CourierAvatar } from './OrderParts.tsx';

import '../styles/orders.css';

const PERCENTS = [0, 3, 5, 10];

/**
 * Оценка заказа и чаевые курьеру: звёзды и выбор процента. `onClose` получает отправленные
 * звёзды или null, если лист закрыли без оценки.
 */
export function RatingSheet(props: { order: OrderListItem; onClose: (stars: number | null) => void }) {
  const { order } = props;
  const strings = useStrings();
  const paymentConfig = useStore(paymentConfigStore);

  const [stars, setStars] = useState(0);
  /** null — своя сумма. */
  const [tipPercent, setTipPercent] = useState<number | null>(0);
  const [customTip, setCustomTip] = useState('');
  const [comment, setComment] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentStars = useRef<number | null>(null);
  const customInput = useRef<HTMLInputElement | null>(null);

  const total = para(order.totalRsd);
  const customActive = tipPercent === null;

  useEffect(() => {
    if (customActive) customInput.current?.focus();
  }, [customActive]);

  const tip = (): bigint | null => {
    if (tipPercent !== null) return tipPercent > 0 ? percentOf(total, tipPercent) : null;
    const value = customTip ? BigInt(customTip) * 100n : 0n;
    return value > 0n ? value : null;
  };

  const submit = async (close: () => void) => {
    if (stars === 0) {
      close();
      return;
    }
    setIsSending(true);
    try {
      const amount = tip();
      const text = comment.trim();
      const result = await ordersApi.rate(order.id, {
        stars,
        ...(amount !== null ? { tipRsd: rsd(amount) } : {}),
        ...(text ? { comment: text } : {}),
      });
      // Оценка сохранена, а карта отказала в чаевых: лист остаётся, повтор — новая попытка.
      if (result.tip?.status === 'declined') {
        setError(strings.tipDeclined);
        return;
      }
      sentStars.current = stars;
      close();
    } catch (cause) {
      setError(errorText(strings, cause));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Sheet onClose={() => props.onClose(sentStars.current)} label={strings.rateOrderTitle}>
      {(close) => (
        <>
          <div className="sheet-header">
            <GlassButton icon="close" label={strings.close} onClick={close} />
          </div>

          <div className="sheet-body">
            <div className="rating-content">
              <div className="stack rating-hero">
                <div className="rating-portrait">
                  {order.courierName ? (
                    <CourierAvatar url={order.courierAvatarUrl} name={order.courierName} size={96} />
                  ) : (
                    <Icon name="check" size={44} weight={3} />
                  )}
                  <span className="rating-portrait-badge">
                    <Icon name="check" size={16} weight={3.4} />
                  </span>
                </div>
                <h2 className="t-display">{strings.deliveredCongrats}</h2>
                {order.courierName && <p className="t-muted t-subheadline">{strings.deliveredBy(order.courierName)}</p>}
              </div>

              <h3 className="t-display rating-title">{strings.rateOrderTitle}</h3>

              <div className="row rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={cx(star <= stars && 'is-on', star === stars && 'is-last')}
                    aria-label={strings.a11yStars(star)}
                    aria-pressed={star === stars}
                    onClick={() => setStars(star)}
                  >
                    <Icon name={star <= stars ? 'starFill' : 'star'} size={40} weight={1.8} />
                  </button>
                ))}
              </div>

              {paymentConfig.cardToken && (
              <div className="stack rating-tips">
                <div className="stack rating-tips-head">
                  <b className="t-display">{strings.tipsTitle}</b>
                  <span className="t-footnote t-muted">{strings.tipsHint}</span>
                </div>
                <div className="rating-tip-row">
                  {PERCENTS.map((percent) => (
                    <button
                      key={percent}
                      type="button"
                      className={cx('rating-tip', tipPercent === percent && 'is-selected')}
                      aria-pressed={tipPercent === percent}
                      onClick={() => {
                        setTipPercent(percent);
                        setCustomTip('');
                      }}
                    >
                      <b>{percent}%</b>
                      <span className="t-numeric">{percent === 0 ? strings.noTipsShort : formatPara(percentOf(total, percent))}</span>
                    </button>
                  ))}
                  {customActive ? (
                    <label className="rating-tip is-selected">
                      <input
                        ref={customInput}
                        className="t-numeric"
                        inputMode="numeric"
                        placeholder="RSD"
                        value={customTip}
                        onChange={(event) => setCustomTip(event.target.value.replace(/\D/g, '').slice(0, 6))}
                      />
                    </label>
                  ) : (
                    <button type="button" className="rating-tip" onClick={() => setTipPercent(null)}>
                      <Icon name="pencil" size={15} weight={2.4} />
                      <span className="rating-tip-other">{strings.tipOther}</span>
                    </button>
                  )}
                </div>
              </div>
              )}

              <textarea
                className="rating-comment"
                rows={2}
                placeholder={strings.ratingCommentPlaceholder}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />

              {error && <ErrorBanner text={error} />}
            </div>
          </div>

          {/* Без звёзд кнопка пропускает оценку и нарисована второстепенной. */}
          <div className="rating-bar">
            <Button kind={stars > 0 ? 'primary' : 'secondary'} busy={isSending} onClick={() => void submit(close)}>
              {stars > 0 ? strings.send : strings.skip}
            </Button>
          </div>
        </>
      )}
    </Sheet>
  );
}
